
import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Table from '../Table/Table'; // Your reusable Table component
import { Eye, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom'; // For navigation links
import config from '../../config';
import debounce from 'lodash.debounce'; // Import lodash debounce

const GlobalSearch = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [filteredDraftHistory, setFilteredDraftHistory] = useState([]);
    const [filteredDelivery, setFilteredDelivery] = useState([]);
    const [filteredHistory, setFilteredHistory] = useState([]);
    const [filteredSuppliers, setFilteredSuppliers] = useState([]);
    const [filteredDraftInvoices, setFilteredDraftInvoices] = useState([]);
    const [filteredDraftProforma, setFilteredDraftProforma] = useState([]);
    const [filteredStockPurchases, setFilteredStockPurchases] = useState([]);
    const [filteredDueCustomers, setFilteredDueCustomers] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]);
    
    const [customerData, setCustomerData] = useState([]);
    const [productData, setProductData] = useState([]);
    const [draftHistoryData, setDraftHistoryData] = useState([]);
    const [deliveryData, setDeliveryData] = useState([]);
    const [historyData, setHistoryData] = useState([]);
    const [supplierData, setSupplierData] = useState([]);
    const [draftInvoiceData, setDraftInvoiceData] = useState([]);
    const [draftProformaData, setDraftProformaData] = useState([]);
    const [stockPurchaseData, setStockPurchaseData] = useState([]);
    const [dueCustomerData, setDueCustomerData] = useState([]);
    const [groupedDueCustomerData, setGroupedDueCustomerData] = useState([]);
    
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Table columns definitions
    const customerColumns = [
        '#', 'Customer Code', 'Name', 'Job', 'Office Name', 'Address', 'Phone Number', 'Email', 'Store', 'Due',
    ];

    const productColumns = [
        'id', 'Product', 'Product Brand', 'Model Number', 'Product Code', 'Weight(g/Kg)', 'Buying Price', 'Selling Price',
        'Warranty (months)', 'Profit', 'Description', 'CategoryId', 'Category', 'Status',
    ];

    const draftHistoryColumns = [
        'ID', 'Invoice No', 'Date/Time', 'Type', 'Customer', 'Address', 'Purchase Order No', 'Store',
        'Transaction Type', 'Total Amount', 'Due', 'Purchase Order img/pdf', 'Print', 'View',
    ];

    const deliveryColumns = [
        'ID', 'Delivery Note No', 'Date/time', 'Type', 'Customer', 'Customer Office', 'Purchase Order No',
        'Store', 'Transaction Type', 'Credit pay', 'Due', 'Purchase Order img/pdf', 'Print', 'View',
    ];

    const historyColumns = [
        'ID', 'Invoice No', 'Date/Time', 'Type', 'Customer', 'Customer Office', 'Purchase Order No', 'Store',
        'Transaction Type', 'Total Amount', 'Due', 'Purchase Order img/pdf', 'Print',
    ];

    const supplierColumns = [
        '#', 'Supplier Name', 'Company', 'Supplier Address', 'NIC', 'Email', 'Contact 1', 'Contact 2', 'Status', 'View',
    ];

    const draftInvoiceColumns = [
        "ID", "Invoice No", "Date/Time", "Type", "Customer", "Address", "Purchase Order No", "Store",
        "Transaction Type", "Total Amount", "Due", "Purchase Order img/pdf", "Draft"
    ];

    const draftProformaColumns = [
        "ID", "Performa Invoice No", "Date/time", "Type", "Customer", 'Customer Office', "Purchase Order No", "store",
        "Transaction Type", "Creadit Pay", "Due", "Purchase Order img/pdf", "Draft"
    ];

    const stockPurchaseColumns = [
        '#', 'Stock Name', 'Stocked Date', 'Supplier Name', 'Product Name', 'Product Code', 'Value'
    ];

    const dueCustomerColumns = ['Customer Code', 'Customer', 'Customer Office', 'Total Amount', 'Total Paid Amount', 'Total Due Amount', 'Actions'];
    const expandedDueCustomerColumns = ['Invoice Number', 'Store', 'Sold Date', 'Total Amount', 'Paid Amount', 'Due Amount', 'Actions'];

    // Fetch functions
    const fetchCustomer = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/customers`);
            if (!response.ok) throw new Error('Failed to fetch Customer list');
            const customers = await response.json();
            setCustomerData(customers);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchProductList = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/products`);
            if (!response.ok) throw new Error(`Failed to fetch product list: ${response.status} ${response.statusText}`);
            const prod = await response.json();
            setProductData(prod);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchDraftHistory = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/invoices`);
            if (!response.ok) throw new Error('Failed to fetch Draft History');
            const invoices = await response.json();
            const filteredInvoices = invoices.filter(invoice => invoice.status === "invoice" && invoice.draft === "true");
            const transactionPromises = filteredInvoices.map(async (invoice) => {
                const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
                return transactionResponse.ok ? await transactionResponse.json() : [];
            });
            const transactionsData = await Promise.all(transactionPromises);
            setDraftHistoryData(filteredInvoices.map((invoice, index) => ({
                invoice, transactions: transactionsData[index] || [],
            })));
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchDelivery = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/invoices`);
            if (!response.ok) throw new Error('Failed to fetch Delivery Invoices');
            const invoices = await response.json();
            const filteredInvoices = invoices.filter(invoice => invoice.status === "delivery" && invoice.draft === "true");
            const transactionPromises = filteredInvoices.map(async (invoice) => {
                const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
                return transactionResponse.ok ? await transactionResponse.json() : [];
            });
            const transactionsData = await Promise.all(transactionPromises);
            setDeliveryData(filteredInvoices.map((invoice, index) => ({
                invoice, transactions: transactionsData[index] || [],
            })));
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchHistory = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/invoices`);
            if (!response.ok) throw new Error('Failed to fetch Sales History');
            const invoices = await response.json();
            const filteredInvoices = invoices
                .filter(invoice => invoice.status === "invoice" && invoice.draft === "false")
                .sort((a, b) => b.invoiceId - a.invoiceId);
            const transactionPromises = filteredInvoices.map(async (invoice) => {
                const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
                return transactionResponse.ok ? await transactionResponse.json() : [];
            });
            const transactionsData = await Promise.all(transactionPromises);
            setHistoryData(filteredInvoices.map((invoice, index) => ({
                invoice, transactions: transactionsData[index] || [],
            })));
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchSuppliers = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/suppliers`);
            if (!response.ok) throw new Error('Failed to fetch supplier list');
            const suppliers = await response.json();
            setSupplierData(suppliers);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchDraftInvoices = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/invoices`);
            if (!response.ok) throw new Error('Failed to fetch Draft Invoices');
            const invoices = await response.json();
            const filteredInvoices = invoices
                .filter(invoice => invoice.status === "invoice" && invoice.draft === "true")
                .sort((a, b) => a.invoiceId - b.invoiceId);
                
            const transactionPromises = filteredInvoices.map(async (invoice) => {
                const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
                return transactionResponse.ok ? await transactionResponse.json() : [];
            });
            
            const transactionsData = await Promise.all(transactionPromises);
            setDraftInvoiceData(filteredInvoices.map((invoice, index) => ({
                invoice, transactions: transactionsData[index] || [],
            })));
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchDraftProforma = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/invoices`);
            if (!response.ok) throw new Error('Failed to fetch Draft Proforma Invoices');
            const invoices = await response.json();
            const filteredInvoices = invoices
                .filter(invoice => invoice.performa === "true" && invoice.draft === "true")
                .sort((a, b) => a.invoiceId - b.invoiceId);
                
            const transactionPromises = filteredInvoices.map(async (invoice) => {
                const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
                return transactionResponse.ok ? await transactionResponse.json() : [];
            });
            
            const transactionsData = await Promise.all(transactionPromises);
            setDraftProformaData(filteredInvoices.map((invoice, index) => ({
                invoice, transactions: transactionsData[index] || [],
            })));
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchStockPurchases = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/stocks`);
            if (!response.ok) throw new Error('Failed to fetch stock list');
            const stock = await response.json();
            setStockPurchaseData(stock);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchDueCustomers = async () => {
        try {
            const response = await fetch(`${config.BASE_URL}/invoices`);
            if (!response.ok) throw new Error('Failed to fetch Sales Invoices');
            const invoices = await response.json();

            const transactionPromises = invoices.map(async (invoice) => {
                const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
                return transactionResponse.ok ? await transactionResponse.json() : [];
            });

            const transactionsData = await Promise.all(transactionPromises);

            const formattedData = invoices.map((invoice, index) => {
                const invoiceTransactions = transactionsData[index] || [];
                const creditTransactions = invoiceTransactions.filter(transaction => transaction.transactionType === 'credit');
                if (creditTransactions.length === 0 || !invoice.customer) return null;
              
                const transactionPaid = creditTransactions.reduce((total, transaction) => total + transaction.paid, 0);
                const transactionDue = creditTransactions.reduce((total, transaction) => total + transaction.due, 0);
                const transactionPrice = creditTransactions.reduce((total, transaction) => total + transaction.price, 0);
              
                return {
                    invoiceId: invoice.invoiceId,
                    invoiceNo: invoice.invoiceNo,
                    customerCode: invoice.customer?.cusCode || "-", 
                    customerName: invoice.customer?.cusName || "-",
                    customerOffice: invoice.customer?.cusOffice || "-",
                    cusId: invoice.customer?.cusId || "-", 
                    store: invoice.store,
                    date: invoice.invoiceDate,
                    totalAmount: transactionPrice,
                    paidAmount: transactionPaid,
                    dueAmount: transactionDue,
                };
            }).filter(Boolean);

            setDueCustomerData(formattedData);
            updateGroupedDueCustomerData(formattedData);
        } catch (err) {
            setError(err.message);
        }
    };

    const updateGroupedDueCustomerData = (currentData) => {
        const grouped = currentData.reduce((acc, item) => {
            const existing = acc.find(row => row.customerCode === item.customerCode);
            if (existing) {
                existing.totalAmount += Number(item.totalAmount);
                existing.paidAmount += Number(item.paidAmount);
                existing.dueAmount += Number(item.dueAmount);
            } else {
                acc.push({
                    ...item,
                    totalAmount: Number(item.totalAmount),
                    paidAmount: Number(item.paidAmount),
                    dueAmount: Number(item.dueAmount),
                    cusOffice: item.cusOffice
                });
            }
            return acc;
        }, []);
        setGroupedDueCustomerData(grouped);
    };

    // Fetch all data on component mount
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            await Promise.all([
                fetchCustomer(),
                fetchProductList(),
                fetchDraftHistory(),
                fetchDelivery(),
                fetchHistory(),
                fetchSuppliers(),
                fetchDraftInvoices(),
                fetchDraftProforma(),
                fetchStockPurchases(),
                fetchDueCustomers(),
            ]);
            setIsLoading(false);
        };
        fetchData();
    }, []);

    // Search function
    const handleSearch = (term) => {
        if (!term) {
            setFilteredCustomers([]);
            setFilteredProducts([]);
            setFilteredDraftHistory([]);
            setFilteredDelivery([]);
            setFilteredHistory([]);
            setFilteredSuppliers([]);
            setFilteredDraftInvoices([]);
            setFilteredDraftProforma([]);
            setFilteredStockPurchases([]);
            setFilteredDueCustomers([]);
            return;
        }

        // Filter Customers
        const filteredCust = customerData.map(cus => ({
            cusId: cus.cusId, cusCode: cus.cusCode, cusName: cus.cusName, cusJob: cus.cusJob,
            cusOffice: cus.cusOffice, cusAddress: cus.cusAddress, cusPhone: cus.cusPhone,
            cusEmail: cus.cusEmail, cusStore: cus.cusStore,
            action: <button className="btn btn-primary" onClick={() => handleViewCustomer(cus.cusId)}><Eye /></button>,
        })).filter(customer =>
            Object.values(customer).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredCustomers(filteredCust);

        // Filter Products
        const filteredProd = productData.map(prod => ({
            productId: prod.productId, productName: prod.productName, productBrand: prod.productBrand,
            productEmi: prod.productEmi, productCode: prod.productCode, productUnit: prod.productUnit,
            productBuyingPrice: prod.productBuyingPrice, productSellingPrice: prod.productSellingPrice,
            productWarranty: prod.productWarranty, productProfit: prod.productProfit,
            productDescription: prod.productDescription, categoryId: prod.category?.categoryId,
            categoryName: prod.category?.categoryName,
            productStatus: (
                <select className="form-control" value={prod.productStatus} onChange={(e) => handleStatusChange(prod.productId, e.target.value)}>
                    <option value="In stock">In stock</option>
                    <option value="Out of Stock">Out of Stock</option>
                </select>
            ),
        })).filter(product =>
            Object.values(product).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredProducts(filteredProd);

        // Filter Draft History
        const filteredDraft = draftHistoryData.map(({ invoice, transactions }) => {
            const invoiceDate = new Date(invoice.invoiceDate);
            const formattedInvoiceDate = `${invoiceDate.getFullYear()}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${String(invoiceDate.getDate()).padStart(2, '0')} ${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')}`;
            const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
            const transactionDue = transactions.reduce((total, t) => total + t.due, 0) || 0;
            const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
            const filename = invoice.image ? invoice.image.split('/').pop() : null;

            return {
                invoiceId: invoice.invoiceId, invoiceNo: invoice.invoiceNo, formattedInvoiceDate, status: invoice.status,
                cusName: invoice.customer.cusName, cusAddress: invoice.customer.cusAddress, purchaseNo: invoice.purchaseNo,
                store: invoice.store, transactionTypes, transactionPrice, transactionDue,
                image: filename ? (
                    <a href={`${config.BASE_URL}/download/invoice/${filename}`} download><button className="btn btn-success"><Download /></button></a>
                ) : "No Image",
                print: <Link to={`/createInvoice/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary">Print</button></Link>,
                view: <Link to={`/salesDetails/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-warning"><Eye /></button></Link>,
            };
        }).filter(item =>
            Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredDraftHistory(filteredDraft);

        // Filter Delivery
        const filteredDel = deliveryData.map(({ invoice, transactions }) => {
            const invoiceDate = new Date(invoice.invoiceDate);
            const formattedInvoiceDate = `${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')} ${String(invoiceDate.getDate()).padStart(2, '0')}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${invoiceDate.getFullYear()}`;
            const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
            const transactionDue = transactions.reduce((total, t) => total + t.due, 0) || 0;
            const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
            const filename = invoice.image ? invoice.image.split('/').pop() : null;
            const currentYear = new Date().getFullYear().toString().slice(-2);

            return {
                invoiceId: invoice.invoiceId, deliveryNoteNo: `DN-${invoice.invoiceNo}-${invoice.invoiceTime}-${currentYear}`,
                formattedInvoiceDate, status: invoice.status, cusName: invoice.customer.cusName,
                cusOffice: invoice.customer.cusOffice, purchaseNo: invoice.purchaseNo, store: invoice.store,
                transactionTypes, transactionPrice, transactionDue,
                image: filename ? (
                    <a href={`${config.BASE_URL}/download/invoice/${filename}`} download><button className="btn btn-success"><Download /></button></a>
                ) : "No Image",
                print: <Link to={`/createDelivery/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary">Print</button></Link>,
                view: <Link to={`/salesDetails/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary"><Eye /></button></Link>,
            };
        }).filter(item =>
            Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredDelivery(filteredDel);

        // Filter History
        const filteredHist = historyData.map(({ invoice, transactions }) => {
            const invoiceDate = new Date(invoice.invoiceDate);
            const formattedInvoiceDate = `${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')} ${String(invoiceDate.getDate()).padStart(2, '0')}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${invoiceDate.getFullYear()}`;
            const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
            const transactionDue = transactions.reduce((total, t) => total + t.due, 0) || 0;
            const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
            const filename = invoice.image ? invoice.image.split('/').pop() : null;

            return {
                invoiceId: invoice.invoiceId, invoiceNo: invoice.invoiceNo, formattedInvoiceDate, status: invoice.status,
                cusName: invoice.customer?.cusName || "N/A", cusOffice: invoice.customer?.cusOffice || "N/A",
                purchaseNo: invoice.purchaseNo || "N/A", store: invoice.store || "N/A", transactionTypes,
                transactionPrice, transactionDue,
                image: filename ? (
                    <a href={`${config.BASE_URL}/download/invoice/${filename}`} download><button className="btn btn-success" style={{ fontSize: "12px" }}><Download /></button></a>
                ) : "No Image",
                print: <Link to={`/createInvoice/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary" style={{ fontSize: "12px" }}>Print</button></Link>,
            };
        }).filter(item =>
            Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredHistory(filteredHist);

        // Filter Suppliers
        const filteredSupp = supplierData.map(supplier => ({
            supplierId: supplier.supplierId, supplierName: supplier.supplierName,
            supplierCompany: supplier.supplierCompany || "-", supplierAddress: supplier.supplierAddress,
            supplierNic: supplier.supplierNic || "-", supplierEmail: supplier.supplierEmail || "-",
            supplierTP: supplier.supplierTP, supplierSecondTP: supplier.supplierSecondTP || "-",
            supplierStatus: (
                <select className="form-control" value={supplier.supplierStatus} onChange={(e) => handleSupplierStatusChange(supplier.supplierId, e.target.value)}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                </select>
            ),
            view: (
                <div className='d-flex' style={{ justifyContent: 'center' }}>
                    <button className='btn btn-info' onClick={() => handleViewSupplier(supplier.supplierId, supplier.supplierName || 'unknown')}><Eye /></button>
                </div>
            ),
        })).filter(supplier =>
            Object.values(supplier).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredSuppliers(filteredSupp);

        // Filter Draft Invoices
        const filteredDraftInvs = draftInvoiceData.map(({ invoice, transactions }) => {
            const invoiceDate = new Date(invoice.invoiceDate);
            const formattedInvoiceDate = `${invoiceDate.getFullYear()}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${String(invoiceDate.getDate()).padStart(2, '0')} ${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')}`;
            const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
            const transactionDue = transactions.reduce((total, t) => total + t.due, 0) || 0;
            const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
            const filename = invoice.image ? invoice.image.split('/').pop() : null;

            return {
                invoiceId: invoice.invoiceId,
                invoiceNo: invoice.invoiceNo,
                formattedInvoiceDate,
                status: invoice.status,
                cusName: invoice.customer?.cusName || "N/A",
                cusAddress: invoice.customer?.cusAddress || "N/A",
                purchaseNo: invoice.purchaseNo || "N/A",
                store: invoice.store || "N/A",
                transactionTypes,
                transactionPrice,
                transactionDue,
                image: filename ? (
                    <a href={`${config.BASE_URL}/download/invoice/${filename}`} download>
                        <button className="btn btn-success"><Download /></button>
                    </a>
                ) : "No Image",
                draft: (
                    <div>
                        <Link to={`/DraftSales/${invoice.invoiceId}/invoice`}>
                            <button className="btn btn-primary" style={{ fontSize: "12px" }}>Draft</button>
                        </Link>
                    </div>
                ),
            };
        }).filter(item =>
            Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredDraftInvoices(filteredDraftInvs);

        // Filter Draft Proforma
        const filteredDraftProfs = draftProformaData.map(({ invoice, transactions }) => {
            const invoiceDate = new Date(invoice.invoiceDate);
            const formattedInvoiceDate = `${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')} ${String(invoiceDate.getDate()).padStart(2, '0')}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${invoiceDate.getFullYear()}`;
            const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
            const transactiondue = transactions.reduce((total, t) => total + t.due, 0) || 0;
            const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
            const filename = invoice.image ? invoice.image.split('/').pop() : null;
            const currentYear = formattedInvoiceDate.toString().slice(-2);

            return {
                invoiceId: invoice.invoiceId,
                performaInvoiceNo: 'PI-' + invoice.invoiceNo + '-' + currentYear,
                formattedInvoiceDate,
                status: invoice.status,
                cusName: invoice.customer?.cusName || "N/A",
                cusOffice: invoice.customer?.cusOffice || "N/A",
                purchaseNo: invoice.purchaseNo || "N/A",
                store: invoice.store || "N/A",
                transactionTypes,
                transactionPrice,
                transactiondue,
                image: filename ? (
                    <a href={`${config.BASE_URL}/download/invoice/${filename}`} download>
                        <button className="btn btn-success"><Download /></button>
                    </a>
                ) : "No Image",
                draft: (
                    <div>
                        <Link to={`/DraftSales/${invoice.invoiceId}/proforma`}>
                            <button className="btn btn-primary" style={{ fontSize: "12px" }}>Draft</button>
                        </Link>
                    </div>
                ),
            };
        }).filter(item =>
            Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredDraftProforma(filteredDraftProfs);

        // Filter Stock Purchases
        const filteredStocks = stockPurchaseData.map((stock) => {
            const stockDate = new Date(stock.stockDate);
            const formattedStockDate = `${stockDate.getFullYear()}-${String(stockDate.getMonth() + 1).padStart(2, '0')}-${String(stockDate.getDate()).padStart(2, '0')} ${String(stockDate.getHours()).padStart(2, '0')}:${String(stockDate.getMinutes()).padStart(2, '0')}`;

            return {
                stockId: stock.stockId,
                stockName: stock.stockName || '-',
                formattedStockDate,
                supplierName: stock.supplier?.supplierName || 'Unknown',
                productName: stock.product?.productName || 'Unknown',
                productCode: stock.product?.productCode || 'Unknown',
                stockPrice: stock.stockPrice,
            };
        }).filter(stock =>
            Object.values(stock).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
        );
        setFilteredStockPurchases(filteredStocks);

        // Filter Due Customers
        const filteredDueCusts = groupedDueCustomerData
            .filter(row => row.dueAmount > 0)
            .filter(row =>
                Object.values(row).some(value => 
                    String(value).toLowerCase().includes(term.toLowerCase())
                )
            );
        setFilteredDueCustomers(filteredDueCusts);
    };

    // Debounced search function
    const debouncedSearch = debounce((term) => handleSearch(term), 300);

    // Handle input change for real-time search
    const handleInputChange = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        debouncedSearch(term);
    };

    // Handle customer view
    const handleViewCustomer = (cusId) => {
        window.location.href = `/cus-due/${cusId}`;
    };

    // Handle product status change
    const handleStatusChange = async (productId, newStatus) => {
        try {
            const response = await fetch(`${config.BASE_URL}/product/${productId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productStatus: newStatus }),
            });
            if (!response.ok) throw new Error('Failed to update product status');
            await fetchProductList();
        } catch (error) {
            setError(error.message);
        }
    };

    // Handle supplier status change
    const handleSupplierStatusChange = async (supplierId, newStatus) => {
        try {
            const response = await fetch(`${config.BASE_URL}/supplier/${supplierId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ supplierStatus: newStatus }),
            });
            if (!response.ok) throw new Error('Failed to update supplier status');
            await fetchSuppliers();
        } catch (error) {
            setError(error.message);
        }
    };

    // Handle supplier view
    const handleViewSupplier = (supplierId, supplierName) => {
        window.location.href = `/supplier-payments/${supplierId}`;
    };

    // Toggle expanded rows for due customers
    const toggleRowExpansion = (customerCode) => {
        if (expandedRows.includes(customerCode)) {
            setExpandedRows(expandedRows.filter(code => code !== customerCode));
        } else {
            setExpandedRows([...expandedRows, customerCode]);
        }
    };

    // Format data for Table component
    const formatTableData = (data) => {
        return data.map(item => Object.values(item));
    };

    // Get expanded details for a due customer
    const getExpandedDueCustomerDetails = (customerCode) => {
        return dueCustomerData
            .filter(item => item.customerCode === customerCode)
            .map((item, index) => ({
                invoiceNo: item.invoiceNo,
                store: item.store,
                date: new Date(item.date).toLocaleString(),
                totalAmount: item.totalAmount,
                paidAmount: item.paidAmount,
                dueAmount: item.dueAmount,
                action: (
                    <button
                        className="btn btn-success"
                        onClick={() => handlePayClick(item)}
                        disabled={item.dueAmount === 0}
                    >
                        Payment
                    </button>
                )
            }));
    };

    // Handle pay click for due customers
    const handlePayClick = (item) => {
        // Implement payment logic here
        console.log("Payment clicked for:", item);
    };

    // Handle view details for due customers
    const handleViewDetails = (cusId) => {
        window.location.href = `/cus-due/${cusId}`;
    };

    return (
        <div className="container mt-4">
            <div className='text-center mb-2'>
                <h2>Global Search</h2>
            </div>
            {isLoading ? (
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading data...</p>
                </div>
            ) : (
                <>
                    <div className="input-group mb-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter search keyword"
                            value={searchTerm}
                            onChange={handleInputChange}
                        />
                        <button className="btn btn-primary" onClick={() => handleSearch(searchTerm)}>Search</button>
                    </div>

                    {error && <p className="text-danger">{error}</p>}

                    {filteredCustomers.length > 0 && (
                        <div className="mt-4">
                            <h3>Customers</h3>
                            <Table data={formatTableData(filteredCustomers)} columns={customerColumns} showButton={false} title="Customer List" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredProducts.length > 0 && (
                        <div className="mt-4">
                            <h3>Products</h3>
                            <Table data={formatTableData(filteredProducts)} columns={productColumns} showButton={false} title="Product List" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredDraftHistory.length > 0 && (
                        <div className="mt-4">
                            <h3>Draft History</h3>
                            <Table data={formatTableData(filteredDraftHistory)} columns={draftHistoryColumns} showButton={false} title="Draft History" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredDelivery.length > 0 && (
                        <div className="mt-4">
                            <h3>Delivery</h3>
                            <Table data={formatTableData(filteredDelivery)} columns={deliveryColumns} showButton={false} title="Delivery" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredHistory.length > 0 && (
                        <div className="mt-4">
                            <h3>History</h3>
                            <Table data={formatTableData(filteredHistory)} columns={historyColumns} showButton={false} title="History" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredSuppliers.length > 0 && (
                        <div className="mt-4">
                            <h3>Suppliers</h3>
                            <Table data={formatTableData(filteredSuppliers)} columns={supplierColumns} showButton={false} title="Suppliers" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredDraftInvoices.length > 0 && (
                        <div className="mt-4">
                            <h3>Draft Invoices</h3>
                            <Table data={formatTableData(filteredDraftInvoices)} columns={draftInvoiceColumns} showButton={false} title="Draft Invoices" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredDraftProforma.length > 0 && (
                        <div className="mt-4">
                            <h3>Draft Proforma Invoices</h3>
                            <Table data={formatTableData(filteredDraftProforma)} columns={draftProformaColumns} showButton={false} title="Draft Proforma Invoices" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredStockPurchases.length > 0 && (
                        <div className="mt-4">
                            <h3>Stock Purchases</h3>
                            <Table data={formatTableData(filteredStockPurchases)} columns={stockPurchaseColumns} showButton={false} title="Stock Purchases" showDate={false} showActions={false} showPDF={false} showSearch={false} />
                        </div>
                    )}

                    {filteredDueCustomers.length > 0 && (
                        <div className="mt-4">
                            <h3>Due Customers</h3>
                            <div className="table-responsive">
                                <table className="table table-bordered due-cus-table">
                                    <thead>
                                        <tr>
                                            {dueCustomerColumns.map((column, index) => (
                                                <th key={index}>{column}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredDueCustomers.map((row) => (
                                            <React.Fragment key={row.customerCode}>
                                                <tr>
                                                    <td>{row.customerCode}</td>
                                                    <td>{row.customerName}</td>
                                                    <td>{row.customerOffice}</td>
                                                    <td>{row.totalAmount.toFixed(2)}</td>
                                                    <td>{row.paidAmount.toFixed(2)}</td>
                                                    <td>{row.dueAmount.toFixed(2)}</td>
                                                    <td>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            
                                                            <button
                                                                className="btn btn-warning ms-2"
                                                                onClick={() => handleViewDetails(row.cusId)}
                                                            >
                                                                <Eye size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expandedRows.includes(row.customerCode) && (
                                                    <tr className="expanded-row">
                                                        <td colSpan={dueCustomerColumns.length}>
                                                            <table className="table table-bordered expanded-table">
                                                                <thead>
                                                                    <tr>
                                                                        {expandedDueCustomerColumns.map((column, index) => (
                                                                            <th key={index}>{column}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {getExpandedDueCustomerDetails(row.customerCode).map((item, index) => (
                                                                        <tr key={index}>
                                                                            <td>{item.invoiceNo}</td>
                                                                            <td>{item.store}</td>
                                                                            <td>{item.date}</td>
                                                                            <td>{item.totalAmount.toFixed(2)}</td>
                                                                            <td>{item.paidAmount.toFixed(2)}</td>
                                                                            <td>{item.dueAmount.toFixed(2)}</td>
                                                                            <td>{item.action}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {searchTerm && 
                        filteredCustomers.length === 0 && 
                        filteredProducts.length === 0 &&
                        filteredDraftHistory.length === 0 && 
                        filteredDelivery.length === 0 &&
                        filteredHistory.length === 0 && 
                        filteredSuppliers.length === 0 &&
                        filteredDraftInvoices.length === 0 &&
                        filteredDraftProforma.length === 0 &&
                        filteredStockPurchases.length === 0 &&
                        filteredDueCustomers.length === 0 && (
                            <p>No results found for "{searchTerm}"</p>
                        )}
                </>
            )}
        </div>
    );
};

export default GlobalSearch;

//v2


// import React, { useState, useEffect } from 'react';
// import 'bootstrap/dist/css/bootstrap.min.css';
// import Table from '../Table/Table'; // Your reusable Table component
// import { Eye, Download } from 'lucide-react';
// import { Link } from 'react-router-dom'; // For navigation links
// import config from '../../config';
// import debounce from 'lodash.debounce'; // Import lodash debounce

// const GlobalSearch = () => {
//     const [searchTerm, setSearchTerm] = useState('');
//     const [filteredCustomers, setFilteredCustomers] = useState([]);
//     const [filteredProducts, setFilteredProducts] = useState([]);
//     const [filteredDraftHistory, setFilteredDraftHistory] = useState([]);
//     const [filteredDelivery, setFilteredDelivery] = useState([]);
//     const [filteredHistory, setFilteredHistory] = useState([]);
//     const [filteredSuppliers, setFilteredSuppliers] = useState([]);
//     const [customerData, setCustomerData] = useState([]);
//     const [productData, setProductData] = useState([]);
//     const [draftHistoryData, setDraftHistoryData] = useState([]);
//     const [deliveryData, setDeliveryData] = useState([]);
//     const [historyData, setHistoryData] = useState([]);
//     const [supplierData, setSupplierData] = useState([]);
//     const [isLoading, setIsLoading] = useState(true); // Loading state for data fetch
//     const [error, setError] = useState(null);

//     // Customer table columns
//     const customerColumns = [
//         '#', 'Customer Code', 'Name', 'Job', 'Office Name', 'Address', 'Phone Number', 'Email', 'Store', 'Due',
//     ];

//     // Product table columns
//     const productColumns = [
//         'id', 'Product', 'Product Brand', 'Model Number', 'Product Code', 'Weight(g/Kg)', 'Buying Price', 'Selling Price',
//         'Warranty (months)', 'Profit', 'Description', 'CategoryId', 'Category', 'Status',
//     ];

//     // Draft History table columns
//     const draftHistoryColumns = [
//         'ID', 'Invoice No', 'Date/Time', 'Type', 'Customer', 'Address', 'Purchase Order No', 'Store',
//         'Transaction Type', 'Total Amount', 'Due', 'Purchase Order img/pdf', 'Print', 'View',
//     ];

//     // Delivery table columns
//     const deliveryColumns = [
//         'ID', 'Delivery Note No', 'Date/time', 'Type', 'Customer', 'Customer Office', 'Purchase Order No',
//         'Store', 'Transaction Type', 'Credit pay', 'Due', 'Purchase Order img/pdf', 'Print', 'View',
//     ];

//     // History table columns
//     const historyColumns = [
//         'ID', 'Invoice No', 'Date/Time', 'Type', 'Customer', 'Customer Office', 'Purchase Order No', 'Store',
//         'Transaction Type', 'Total Amount', 'Due', 'Purchase Order img/pdf', 'Print',
//     ];

//     // Supplier table columns
//     const supplierColumns = [
//         '#', 'Supplier Name', 'Company', 'Supplier Address', 'NIC', 'Email', 'Contact 1', 'Contact 2', 'Status', 'View',
//     ];

//     // Fetch customer data
//     const fetchCustomer = async () => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/customers`);
//             if (!response.ok) throw new Error('Failed to fetch Customer list');
//             const customers = await response.json();
//             setCustomerData(customers);
//         } catch (err) {
//             setError(err.message);
//         }
//     };

//     // Fetch product data
//     const fetchProductList = async () => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/products`);
//             if (!response.ok) throw new Error(`Failed to fetch product list: ${response.status} ${response.statusText}`);
//             const prod = await response.json();
//             setProductData(prod);
//         } catch (err) {
//             setError(err.message);
//         }
//     };

//     // Fetch Draft History data
//     const fetchDraftHistory = async () => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/invoices`);
//             if (!response.ok) throw new Error('Failed to fetch Draft History');
//             const invoices = await response.json();
//             const filteredInvoices = invoices.filter(invoice => invoice.status === "invoice" && invoice.draft === "true");
//             const transactionPromises = filteredInvoices.map(async (invoice) => {
//                 const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
//                 return transactionResponse.ok ? await transactionResponse.json() : [];
//             });
//             const transactionsData = await Promise.all(transactionPromises);
//             setDraftHistoryData(filteredInvoices.map((invoice, index) => ({
//                 invoice, transactions: transactionsData[index] || [],
//             })));
//         } catch (err) {
//             setError(err.message);
//         }
//     };

//     // Fetch Delivery data
//     const fetchDelivery = async () => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/invoices`);
//             if (!response.ok) throw new Error('Failed to fetch Delivery Invoices');
//             const invoices = await response.json();
//             const filteredInvoices = invoices.filter(invoice => invoice.status === "delivery" && invoice.draft === "true");
//             const transactionPromises = filteredInvoices.map(async (invoice) => {
//                 const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
//                 return transactionResponse.ok ? await transactionResponse.json() : [];
//             });
//             const transactionsData = await Promise.all(transactionPromises);
//             setDeliveryData(filteredInvoices.map((invoice, index) => ({
//                 invoice, transactions: transactionsData[index] || [],
//             })));
//         } catch (err) {
//             setError(err.message);
//         }
//     };

//     // Fetch History data
//     const fetchHistory = async () => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/invoices`);
//             if (!response.ok) throw new Error('Failed to fetch Sales History');
//             const invoices = await response.json();
//             const filteredInvoices = invoices
//                 .filter(invoice => invoice.status === "invoice" && invoice.draft === "false")
//                 .sort((a, b) => b.invoiceId - a.invoiceId);
//             const transactionPromises = filteredInvoices.map(async (invoice) => {
//                 const transactionResponse = await fetch(`${config.BASE_URL}/transaction/invoice/${invoice.invoiceId}`);
//                 return transactionResponse.ok ? await transactionResponse.json() : [];
//             });
//             const transactionsData = await Promise.all(transactionPromises);
//             setHistoryData(filteredInvoices.map((invoice, index) => ({
//                 invoice, transactions: transactionsData[index] || [],
//             })));
//         } catch (err) {
//             setError(err.message);
//         }
//     };

//     // Fetch Supplier data
//     const fetchSuppliers = async () => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/suppliers`);
//             if (!response.ok) throw new Error('Failed to fetch supplier list');
//             const suppliers = await response.json();
//             setSupplierData(suppliers);
//         } catch (err) {
//             setError(err.message);
//         }
//     };

//     // Fetch all data on component mount
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             await Promise.all([
//                 fetchCustomer(),
//                 fetchProductList(),
//                 fetchDraftHistory(),
//                 fetchDelivery(),
//                 fetchHistory(),
//                 fetchSuppliers(),
//             ]);
//             setIsLoading(false);
//         };
//         fetchData();
//     }, []);

//     // Search function
//     const handleSearch = (term) => {
//         if (!term) {
//             setFilteredCustomers([]);
//             setFilteredProducts([]);
//             setFilteredDraftHistory([]);
//             setFilteredDelivery([]);
//             setFilteredHistory([]);
//             setFilteredSuppliers([]);
//             return;
//         }

//         // Filter Customers
//         const filteredCust = customerData.map(cus => ({
//             cusId: cus.cusId, cusCode: cus.cusCode, cusName: cus.cusName, cusJob: cus.cusJob,
//             cusOffice: cus.cusOffice, cusAddress: cus.cusAddress, cusPhone: cus.cusPhone,
//             cusEmail: cus.cusEmail, cusStore: cus.cusStore,
//             action: <button className="btn btn-primary" onClick={() => handleViewCustomer(cus.cusId)}><Eye /></button>,
//         })).filter(customer =>
//             Object.values(customer).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
//         );
//         setFilteredCustomers(filteredCust);

//         // Filter Products
//         const filteredProd = productData.map(prod => ({
//             productId: prod.productId, productName: prod.productName, productBrand: prod.productBrand,
//             productEmi: prod.productEmi, productCode: prod.productCode, productUnit: prod.productUnit,
//             productBuyingPrice: prod.productBuyingPrice, productSellingPrice: prod.productSellingPrice,
//             productWarranty: prod.productWarranty, productProfit: prod.productProfit,
//             productDescription: prod.productDescription, categoryId: prod.category?.categoryId,
//             categoryName: prod.category?.categoryName,
//             productStatus: (
//                 <select className="form-control" value={prod.productStatus} onChange={(e) => handleStatusChange(prod.productId, e.target.value)}>
//                     <option value="In stock">In stock</option>
//                     <option value="Out of Stock">Out of Stock</option>
//                 </select>
//             ),
//         })).filter(product =>
//             Object.values(product).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
//         );
//         setFilteredProducts(filteredProd);

//         // Filter Draft History
//         const filteredDraft = draftHistoryData.map(({ invoice, transactions }) => {
//             const invoiceDate = new Date(invoice.invoiceDate);
//             const formattedInvoiceDate = `${invoiceDate.getFullYear()}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${String(invoiceDate.getDate()).padStart(2, '0')} ${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')}`;
//             const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
//             const transactionDue = transactions.reduce((total, t) => total + t.due, 0) || 0;
//             const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
//             const filename = invoice.image ? invoice.image.split('/').pop() : null;

//             return {
//                 invoiceId: invoice.invoiceId, invoiceNo: invoice.invoiceNo, formattedInvoiceDate, status: invoice.status,
//                 cusName: invoice.customer.cusName, cusAddress: invoice.customer.cusAddress, purchaseNo: invoice.purchaseNo,
//                 store: invoice.store, transactionTypes, transactionPrice, transactionDue,
//                 image: filename ? (
//                     <a href={`${config.BASE_URL}/download/invoice/${filename}`} download><button className="btn btn-success"><Download /></button></a>
//                 ) : "No Image",
//                 print: <Link to={`/createInvoice/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary">Print</button></Link>,
//                 view: <Link to={`/salesDetails/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-warning"><Eye /></button></Link>,
//             };
//         }).filter(item =>
//             Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
//         );
//         setFilteredDraftHistory(filteredDraft);

//         // Filter Delivery
//         const filteredDel = deliveryData.map(({ invoice, transactions }) => {
//             const invoiceDate = new Date(invoice.invoiceDate);
//             const formattedInvoiceDate = `${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')} ${String(invoiceDate.getDate()).padStart(2, '0')}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${invoiceDate.getFullYear()}`;
//             const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
//             const transactionDue = transactions.reduce((total, t) => total + t.due, 0) || 0;
//             const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
//             const filename = invoice.image ? invoice.image.split('/').pop() : null;
//             const currentYear = new Date().getFullYear().toString().slice(-2);

//             return {
//                 invoiceId: invoice.invoiceId, deliveryNoteNo: `DN-${invoice.invoiceNo}-${invoice.invoiceTime}-${currentYear}`,
//                 formattedInvoiceDate, status: invoice.status, cusName: invoice.customer.cusName,
//                 cusOffice: invoice.customer.cusOffice, purchaseNo: invoice.purchaseNo, store: invoice.store,
//                 transactionTypes, transactionPrice, transactionDue,
//                 image: filename ? (
//                     <a href={`${config.BASE_URL}/download/invoice/${filename}`} download><button className="btn btn-success"><Download /></button></a>
//                 ) : "No Image",
//                 print: <Link to={`/createDelivery/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary">Print</button></Link>,
//                 view: <Link to={`/salesDetails/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary"><Eye /></button></Link>,
//             };
//         }).filter(item =>
//             Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
//         );
//         setFilteredDelivery(filteredDel);

//         // Filter History
//         const filteredHist = historyData.map(({ invoice, transactions }) => {
//             const invoiceDate = new Date(invoice.invoiceDate);
//             const formattedInvoiceDate = `${String(invoiceDate.getHours()).padStart(2, '0')}:${String(invoiceDate.getMinutes()).padStart(2, '0')} ${String(invoiceDate.getDate()).padStart(2, '0')}-${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${invoiceDate.getFullYear()}`;
//             const transactionPrice = transactions.reduce((total, t) => total + t.paid, 0) || 0;
//             const transactionDue = transactions.reduce((total, t) => total + t.due, 0) || 0;
//             const transactionTypes = transactions.map(t => t.transactionType).join(', ') || "-";
//             const filename = invoice.image ? invoice.image.split('/').pop() : null;

//             return {
//                 invoiceId: invoice.invoiceId, invoiceNo: invoice.invoiceNo, formattedInvoiceDate, status: invoice.status,
//                 cusName: invoice.customer?.cusName || "N/A", cusOffice: invoice.customer?.cusOffice || "N/A",
//                 purchaseNo: invoice.purchaseNo || "N/A", store: invoice.store || "N/A", transactionTypes,
//                 transactionPrice, transactionDue,
//                 image: filename ? (
//                     <a href={`${config.BASE_URL}/download/invoice/${filename}`} download><button className="btn btn-success" style={{ fontSize: "12px" }}><Download /></button></a>
//                 ) : "No Image",
//                 print: <Link to={`/createInvoice/${invoice.store}/${invoice.invoiceNo}`}><button className="btn btn-primary" style={{ fontSize: "12px" }}>Print</button></Link>,
//             };
//         }).filter(item =>
//             Object.values(item).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
//         );
//         setFilteredHistory(filteredHist);

//         // Filter Suppliers
//         const filteredSupp = supplierData.map(supplier => ({
//             supplierId: supplier.supplierId, supplierName: supplier.supplierName,
//             supplierCompany: supplier.supplierCompany || "-", supplierAddress: supplier.supplierAddress,
//             supplierNic: supplier.supplierNic || "-", supplierEmail: supplier.supplierEmail || "-",
//             supplierTP: supplier.supplierTP, supplierSecondTP: supplier.supplierSecondTP || "-",
//             supplierStatus: (
//                 <select className="form-control" value={supplier.supplierStatus} onChange={(e) => handleSupplierStatusChange(supplier.supplierId, e.target.value)}>
//                     <option value="Active">Active</option>
//                     <option value="Inactive">Inactive</option>
//                 </select>
//             ),
//             view: (
//                 <div className='d-flex' style={{ justifyContent: 'center' }}>
//                     <button className='btn btn-info' onClick={() => handleViewSupplier(supplier.supplierId, supplier.supplierName || 'unknown')}><Eye /></button>
//                 </div>
//             ),
//         })).filter(supplier =>
//             Object.values(supplier).some(value => String(value).toLowerCase().includes(term.toLowerCase()))
//         );
//         setFilteredSuppliers(filteredSupp);
//     };

//     // Debounced search function
//     const debouncedSearch = debounce((term) => handleSearch(term), 300);

//     // Handle input change for real-time search
//     const handleInputChange = (e) => {
//         const term = e.target.value;
//         setSearchTerm(term);
//         debouncedSearch(term);
//     };

//     // Handle customer view
//     const handleViewCustomer = (cusId) => {
//         window.location.href = `/cus-due/${cusId}`; // Replace with navigate if using react-router
//     };

//     // Handle product status change
//     const handleStatusChange = async (productId, newStatus) => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/product/${productId}`, {
//                 method: 'PUT',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ productStatus: newStatus }),
//             });
//             if (!response.ok) throw new Error('Failed to update product status');
//             await fetchProductList();
//         } catch (error) {
//             setError(error.message);
//         }
//     };

//     // Handle supplier status change
//     const handleSupplierStatusChange = async (supplierId, newStatus) => {
//         try {
//             const response = await fetch(`${config.BASE_URL}/supplier/${supplierId}`, {
//                 method: 'PUT',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ supplierStatus: newStatus }),
//             });
//             if (!response.ok) throw new Error('Failed to update supplier status');
//             await fetchSuppliers();
//         } catch (error) {
//             setError(error.message);
//         }
//     };

//     // Handle supplier view
//     const handleViewSupplier = (supplierId, supplierName) => {
//         window.location.href = `/supplier-payments/${supplierId}`; // Replace with navigate if using react-router
//     };

//     // Format data for Table component
//     const formatTableData = (data) => {
//         return data.map(item => Object.values(item));
//     };

//     return (
//         <div className="container mt-4">
//             <div className='text-center mb-2'>
//                 <h2>Global Search</h2>
//             </div>
//             {isLoading ? (
//                 <div className="text-center">
//                     <div className="spinner-border text-primary" role="status">
//                         <span className="visually-hidden">Loading...</span>
//                     </div>
//                     <p>Loading data...</p>
//                 </div>
//             ) : (
//                 <>
//                     <div className="input-group mb-3">
//                         <input
//                             type="text"
//                             className="form-control"
//                             placeholder="Enter search keyword"
//                             value={searchTerm}
//                             onChange={handleInputChange}
//                         />
//                         <button className="btn btn-primary" onClick={() => handleSearch(searchTerm)}>Search</button>
//                     </div>

//                     {error && <p className="text-danger">{error}</p>}

//                     {filteredCustomers.length > 0 && (
//                         <div className="mt-4">
//                             <h3>Customers</h3>
//                             <Table data={formatTableData(filteredCustomers)} columns={customerColumns} showButton={false} title="Customer List" showDate={false} showActions={false} showPDF={false} showSearch={false} />
//                         </div>
//                     )}

//                     {filteredProducts.length > 0 && (
//                         <div className="mt-4">
//                             <h3>Products</h3>
//                             <Table data={formatTableData(filteredProducts)} columns={productColumns} showButton={false} title="Product List" showDate={false} showActions={false} showPDF={false} showSearch={false} />
//                         </div>
//                     )}

//                     {filteredDraftHistory.length > 0 && (
//                         <div className="mt-4">
//                             <h3>Draft History</h3>
//                             <Table data={formatTableData(filteredDraftHistory)} columns={draftHistoryColumns} showButton={false} title="Draft History" showDate={false} showActions={false} showPDF={false} showSearch={false} />
//                         </div>
//                     )}

//                     {filteredDelivery.length > 0 && (
//                         <div className="mt-4">
//                             <h3>Delivery</h3>
//                             <Table data={formatTableData(filteredDelivery)} columns={deliveryColumns} showButton={false} title="Delivery" showDate={false} showActions={false} showPDF={false} showSearch={false} />
//                         </div>
//                     )}

//                     {filteredHistory.length > 0 && (
//                         <div className="mt-4">
//                             <h3>History</h3>
//                             <Table data={formatTableData(filteredHistory)} columns={historyColumns} showButton={false} title="History" showDate={false} showActions={false} showPDF={false} showSearch={false} />
//                         </div>
//                     )}

//                     {filteredSuppliers.length > 0 && (
//                         <div className="mt-4">
//                             <h3>Suppliers</h3>
//                             <Table data={formatTableData(filteredSuppliers)} columns={supplierColumns} showButton={false} title="Suppliers" showDate={false} showActions={false} showPDF={false} showSearch={false} />
//                         </div>
//                     )}

//                     {searchTerm && filteredCustomers.length === 0 && filteredProducts.length === 0 &&
//                         filteredDraftHistory.length === 0 && filteredDelivery.length === 0 &&
//                         filteredHistory.length === 0 && filteredSuppliers.length === 0 && (
//                             <p>No results found for "{searchTerm}"</p>
//                         )}
//                 </>
//             )}
//         </div>
//     );
// };

// export default GlobalSearch;

